import React, { useEffect } from 'react';
import _ from 'underscore';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker';
import { SelectField } from 'components';
import validate from 'validate.js';
import { WorstSitesRequest, SetFormState } from '../../actions/WorstSitesActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	subRegion: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	startDate: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	endDate: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const SubRegions = [
	{ value: 'All Regions', text: 'All Regions' },
	{ value: 'Central Region -A', text: 'Central Region - A' },
	{ value: 'Central Region - B', text: 'Central Region - B' },
	{ value: 'Central Region -C', text: 'Central Region - C' },
	{ value: 'London - A', text: 'London - A' },
	{ value: 'London - B', text: 'London - B' },
	{ value: 'London - C', text: 'London - C' },
	{ value: 'London - D', text: 'London - D' },
	{ value: 'Scotland', text: 'Scotland' },
	{ value: 'South Region - A', text: 'South Region - A' },
	{ value: 'South Region - B', text: 'South Region - B' },
	{ value: 'South England - C', text: 'South England - C' },
	{ value: 'Northern Ireland', text: 'Northern Ireland' },
	{ value: 'North Region - C', text: 'North Region - C' },
	{ value: 'North Region - B', text: 'North Region - B' },
	{ value: 'North Region - A', text: 'North Region - A' }
];

const TopOffendersWorstForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.TopOffendersWorstSitesReducers.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		const errors = validate(formState.values, schema);
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			}
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generate = () => {
		dispatch(WorstSitesRequest(formState.values, user.id));
	};

	return (
		<Card className={classes.content}>
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={2} xs={12}>
						<TextField
							fullWidth
							label="TOP"
							name="topSites"
							onChange={_handleChange}
							value={formState.values.topSites}
							variant="outlined"
							error={_hasError('topSites')}
							helperText={
								_hasError('topSites') ? formState.errors.topSites[0] : 'Example: 10'
							}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<SelectField
							id="subRegion"
							name="subRegion"
							value={formState.values.subRegion || ''}
							onChange={_handleChange}
							hintText="Sub Regions"
							menuItems={SubRegions}
							error={_hasError('subRegion')}
							helperText={_hasError('subRegion') ? formState.errors.subRegion[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<DatePicker
							label="Start Date"
							name="startDate"
							value={formState.values.startDate || null}
							onChange={(value) => _handleDateChange(value, 'startDate')}
							error={_hasError('startDate')}
							helperText={_hasError('startDate') ? formState.errors.startDate[0] : null}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<DatePicker
							label="End Date"
							name="endDate"
							value={formState.values.endDate || null}
							onChange={(value) => _handleDateChange(value, 'endDate')}
							error={_hasError('endDate')}
							helperText={_hasError('endDate') ? formState.errors.endDate[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default TopOffendersWorstForm;
